import confetti from 'canvas-confetti';
import { DateTime } from 'luxon';

// Lets QA see the Halloween extras on any date: visit any page with ?halloween=on
// (and ?halloween=off to go back to the real calendar).
const PREVIEW_STORAGE_KEY = 'tidy_halloween_preview';
const SPOOKY_EMOJI = ['🦇', '🎃', '👻', '🕷️'];
// Emoji are rasterized once at this scale; firing them at a different scalar blurs them.
const EMOJI_SCALAR = 2;

let isActiveCache: boolean | null = null;
let spookyShapes: any[] | null = null;

export function isHalloweenActive(): boolean {
  if (isActiveCache === null) {
    isActiveCache = readPreviewOverride(window.location.href) ?? isHalloweenWeek(DateTime.now());
  }
  return isActiveCache;
}

export function isHalloweenWeek(date: DateTime): boolean {
  return date.month === 10 && date.day >= 28 && date.day <= 31;
}

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/** Swaps confetti pieces for bats, pumpkins, ghosts and spiders during Halloween week. */
export function spookyConfetti<T extends Record<string, any>>(options: T): T {
  if (!isHalloweenActive()) {
    return options;
  }
  const shapes = getSpookyShapes();
  if (!shapes.length) {
    return options;
  }
  // Emoji are much bigger than paper pieces, so fewer of them fill the same burst.
  const particleCount = Math.max(12, Math.round((options['particleCount'] ?? 50) * 0.4));
  return { ...options, particleCount, shapes, scalar: EMOJI_SCALAR, flat: true };
}

export function getSpookyShapes(): any[] {
  if (spookyShapes === null) {
    try {
      spookyShapes = SPOOKY_EMOJI.map((text) => confetti.shapeFromText({ text, scalar: EMOJI_SCALAR }));
    } catch (_error) {
      // Canvas text rendering unavailable; fall back to regular confetti.
      spookyShapes = [];
    }
  }
  return spookyShapes;
}

/** Applies a ?halloween=on|off in the URL, then reports the stored QA preview (null = follow the calendar). */
export function readPreviewOverride(href: string): boolean | null {
  try {
    const match = href.match(/[?&]halloween=(on|off)\b/);
    if (match?.[1] === 'on') {
      localStorage.setItem(PREVIEW_STORAGE_KEY, 'on');
    } else if (match?.[1] === 'off') {
      localStorage.removeItem(PREVIEW_STORAGE_KEY);
    }
    return localStorage.getItem(PREVIEW_STORAGE_KEY) === 'on' ? true : null;
  } catch (_error) {
    return null;
  }
}

/** Test-only: forget the cached answer so each spec can pick its own date/override. */
export function resetHalloweenCacheForTests(): void {
  isActiveCache = null;
  spookyShapes = null;
}
